import { Link } from "react-router-dom";
import { FiArrowUp, FiArrowDown, FiExternalLink } from "react-icons/fi";
import { MdDragIndicator, MdOutlinePushPin } from "react-icons/md";

import ListingThumb from "./ListingThumb";

const CATEGORY_TONES = {
  Venue: "bg-sky-50 text-sky-700 ring-sky-200",
  Restaurant: "bg-amber-50 text-amber-700 ring-amber-200",
  Hotel: "bg-violet-50 text-violet-700 ring-violet-200",
};

/** The admin preview routes are split by category, same as the venue table. */
const previewPathFor = (item) =>
  `/${item.category === "Hotel" ? "hotel-preview" : "venue-preview"}/${item.category}/${item._id}`;

/**
 * One card in the pinned rail.
 *
 * The whole card is the drag target — a small grip is fussy to hit and the
 * cards are the thing being ordered. The arrow buttons do the same job for
 * keyboard users and for the single-step nudges that are awkward to drag.
 */
const PinnedCard = ({
  item,
  index,
  total,
  image,
  isDragging,
  canManage,
  dragProps,
  onMoveUp,
  onMoveDown,
  onUnpin,
}) => (
  <article
    {...dragProps}
    aria-label={`Position ${index + 1}: ${item.name || "Unnamed listing"}`}
    className={`group relative bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
      isDragging
        ? "border-primary ring-2 ring-primary/40 opacity-60 rotate-1"
        : "border-gray-100 hover:shadow-md hover:border-gray-200"
    } ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
  >
    <div className="relative h-36 w-full">
      <ListingThumb
        src={image}
        alt={item.name}
        category={item.category}
        className="h-full w-full"
      />

      {/* Position is the point of this screen, so it reads as the card's title. */}
      <span className="absolute top-2 left-2 min-w-[28px] h-7 px-2 rounded-lg bg-neutralBlack/80 text-white text-xs font-bold flex items-center justify-center backdrop-blur-sm">
        {index + 1}
      </span>

      <span
        className={`absolute top-2 right-2 rounded-full ring-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
          CATEGORY_TONES[item.category] || "bg-gray-50 text-gray-600 ring-gray-200"
        }`}
      >
        {item.category}
      </span>

      {canManage && (
        <span className="absolute bottom-2 right-2 w-7 h-7 rounded-lg bg-white/85 text-gray-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <MdDragIndicator />
        </span>
      )}
    </div>

    <div className="p-3">
      <h3
        className="text-sm font-semibold text-gray-800 line-clamp-1"
        title={item.name || "Unnamed listing"}
      >
        {item.name || "Unnamed listing"}
      </h3>
      <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
        {[item.city, item.area].filter(Boolean).join(" · ") || "No location on file"}
      </p>
      {item.vendorEmail && (
        <p className="text-[11px] text-gray-400 line-clamp-1 mt-0.5" title={item.vendorEmail}>
          {item.vendorEmail}
        </p>
      )}

      <div className="flex items-center justify-between gap-1 mt-3 pt-2.5 border-t border-gray-100">
        <div className="flex items-center gap-1">
          {canManage && (
            <>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                aria-label="Move up one position"
                title="Move up"
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <FiArrowUp className="text-[13px]" />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === total - 1}
                aria-label="Move down one position"
                title="Move down"
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <FiArrowDown className="text-[13px]" />
              </button>
            </>
          )}
          <Link
            to={previewPathFor(item)}
            // Dragging a link starts a link-drag instead of the card drag.
            draggable={false}
            aria-label="Open the listing preview"
            title="Preview listing"
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <FiExternalLink className="text-[13px]" />
          </Link>
        </div>

        {canManage && (
          <button
            type="button"
            onClick={onUnpin}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-gray-500 hover:bg-red-50 hover:text-red-600"
          >
            <MdOutlinePushPin className="text-[13px] rotate-45" /> Unpin
          </button>
        )}
      </div>
    </div>
  </article>
);

export default PinnedCard;
